import { Box, Link, TableCell, TableRow } from "@mui/material";
import type { SalesOrgLeadCard } from "@xop/dashboard-shared";
import type { JSX } from "react";
import { Link as RouterLink } from "react-router-dom";

import { relativeTime } from "../../../../lib/relativeTime.js";
import { leadHeatDisplay } from "../../leads/heatStyles.js";
import { UrgencyBadge } from "../../leads/UrgencyBadge.js";
import { LeadSourceBadge } from "../LeadSourceBadge.js";
import { VerticalBadge } from "../VerticalBadge.js";
import {
  ConfidenceCell,
  ExistingCustomerCell,
  HeatPill,
  PlaceholderCell,
  QualityPill,
  RfpCell,
  StatusPill,
} from "./cells.js";
import { EditableStatusCell, EditableVerticalCell } from "./editors.js";
import { absoluteTitle } from "./sortLeads.js";
import { cellSx } from "./tableStyles.js";

/* ── Row ───────────────────────────────────────────────────────── */

export function LeadRow({
  lead, compact, showStatus, showQuality, editable, cols,
}: {
  lead: SalesOrgLeadCard;
  compact: boolean;
  showStatus: boolean;
  showQuality: boolean;
  editable: boolean;
  /** Which data-gated placeholder columns are present (computed once from the
   * whole list so every row matches the header). */
  cols: { timeline: boolean; budget: boolean; score: boolean };
}): JSX.Element {
  const conf = Math.round(Math.max(0, Math.min(1, lead.confidence)) * 100);

  return (
    <TableRow
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        "&:last-of-type": { borderBottom: 0 },
        transition: "background-color 150ms",
        "&:hover": { bgcolor: "surfaceSoft" },
      }}
    >
      <TableCell sx={cellSx}>
        {/* The heat pill folds in live deadline urgency — a warm lead whose
          * window is closing reads SUPER HOT / LAST CHANCE (see `leadHeatDisplay`). */}
        <HeatPill display={leadHeatDisplay(lead)} />
      </TableCell>
      <TableCell sx={cellSx}>
        <Link
          component={RouterLink}
          to={`/sales/leads/${lead.id}`}
          underline="hover"
          title={lead.name}
          sx={{
            display: "block",
            fontWeight: 600,
            color: "text.primary",
            textUnderlineOffset: 4,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {lead.name}
        </Link>
        {/* Live deadline-urgency badge beside the name. Provenance moved to its
            own "Found by" column (2026-06-21); `&:empty` hides this row when the
            lead has no live urgency so the name isn't left with a dangling gap. */}
        <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", "&:empty": { display: "none" } }}>
          <UrgencyBadge lead={lead} size="sm" />
        </Box>
        {lead.agent_summary ? (
          <Box
            sx={{
              mt: 0.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: 12,
              color: "text.disabled",
            }}
          >
            {lead.agent_summary}
          </Box>
        ) : null}
      </TableCell>
      <TableCell sx={[cellSx, { fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }]}>
        {lead.country || "—"}
      </TableCell>
      {/* Found by — which AI agent produced this lead (its own column now). */}
      <TableCell sx={cellSx}>
        <LeadSourceBadge source={lead.source} />
      </TableCell>
      <TableCell sx={cellSx}>
        {editable ? (
          <EditableVerticalCell lead={lead} />
        ) : (
          <VerticalBadge vertical={lead.vertical} />
        )}
      </TableCell>
      {!compact && (
        <>
          <TableCell sx={cellSx}>
            <ExistingCustomerCell lead={lead} />
          </TableCell>
          <TableCell sx={cellSx}>
            <RfpCell lead={lead} />
          </TableCell>
          {cols.timeline && (
            <TableCell sx={cellSx}>
              <PlaceholderCell value={lead.project_timeline} />
            </TableCell>
          )}
          {cols.budget && (
            <TableCell sx={cellSx}>
              <PlaceholderCell value={lead.project_budget} />
            </TableCell>
          )}
          {cols.score && (
            <TableCell sx={cellSx}>
              <PlaceholderCell
                value={lead.lead_score != null ? String(lead.lead_score) : null}
              />
            </TableCell>
          )}
        </>
      )}
      {!showQuality && (
        <TableCell sx={cellSx}>
          <ConfidenceCell pct={conf} />
        </TableCell>
      )}
      <TableCell sx={[cellSx, { fontSize: 12, color: "text.disabled", whiteSpace: "nowrap" }]}>
        <Box component="span" title={absoluteTitle(lead.created_at)}>
          {relativeTime(lead.created_at) || "—"}
        </Box>
      </TableCell>
      {!compact && (
        <TableCell sx={[cellSx, { fontSize: 12, color: "text.disabled" }]}>
          {relativeTime(lead.last_signal_at) || "—"}
        </TableCell>
      )}
      {showQuality && (
        <TableCell sx={cellSx}>
          <QualityPill lead={lead} />
        </TableCell>
      )}
      {showStatus && (
        <TableCell sx={[cellSx, { textAlign: "right" }]}>
          {editable && !lead.converted_at ? (
            <EditableStatusCell lead={lead} />
          ) : (
            <StatusPill lead={lead} />
          )}
        </TableCell>
      )}
    </TableRow>
  );
}
